import * as React from "react";
import { MoreHorizontal } from "lucide-react";

import { cn, getTextColorByBgColor } from "@/lib/utils";
import { ButtonProps, buttonVariants } from "@/components/ui/button";
import Arrow from "@/components/icons/Arrow";
import Link from "next/link";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("flex flex-row items-center gap-1", className)} {...props} />
  )
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
  variant?: "default" | "secondary" | "jsc-sky-blue-white" | "outline" | "ghost" | "nsc-mint" | "link";
  disabled?: boolean;
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<typeof Link>;

const PaginationLink = ({
  className,
  isActive,
  disabled,
  variant = "ghost",
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <Link
    aria-current={isActive ? "page" : undefined}
    aria-disabled={disabled ? "true" : undefined}
    className={cn(
      buttonVariants({
        variant: variant as any,
        size,
      }),
      className,
      { "pointer-events-none opacity-50": disabled }
    )}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  variant,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ga naar vorige pagina"
    size="round"
    variant={variant}
    disabled={disabled}
    className={cn("", className)}
    {...props}
  >
    {variant === "jsc-sky-blue-white" && (
      <Arrow direction="left" color={getTextColorByBgColor(variant)} />
    )}

    {variant !== "jsc-sky-blue-white" && (
      <Arrow direction="left" />
    )}
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({ className, variant, disabled, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ga naar volgende pagina"
    size="round"
    variant={variant}
    disabled={disabled}
    className={cn("", className)}
    {...props}
  >
    {variant === "jsc-sky-blue-white" && (
      <Arrow direction="right" color={getTextColorByBgColor(variant)} />
    )}

    {variant !== "jsc-sky-blue-white" && (
      <Arrow direction="right" />
    )}
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span aria-hidden className={cn("flex h-9 w-9 items-center justify-center", className)} {...props}>
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
